import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamApi, subscriptionApi } from '../lib/api';
import { PlusIcon, ExclamationTriangleIcon, ChevronRightIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import InstitutionSetupWizard from '../components/InstitutionSetupWizard';

// Maps the stored primaryRegulator code to the examiner label shown on cards.
const REGULATOR_LABEL: Record<string, string> = {
  FDIC: 'FDIC',
  OCC: 'OCC',
  FED: 'Federal Reserve',
  NCUA: 'NCUA',
};

export default function TeamsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [teamName, setTeamName] = useState('');
  // After creating a bank we immediately open Institution Setup for it so the
  // analyst sets the regulator/charter right away (per-bank correctness).
  const [setupTeamId, setSetupTeamId] = useState<string | null>(null);

  const { data: teamsData, isLoading: teamsLoading, error: teamsError } = useQuery({
    queryKey: ['teams', 'list-unwrapped'],
    queryFn: async () => {
      const response = await teamApi.getTeams();
      return response.data;
    },
  });

  const teams = teamsData?.teams || [];

  // Fetch plan info for team limits
  const { data: planInfo } = useQuery({
    queryKey: ['subscription-plan'],
    queryFn: async () => {
      const response = await subscriptionApi.getPlan();
      return response.data;
    },
    enabled: !!user,
  });

  const teamMemberLimit = planInfo?.limits?.maxUsers;
  const currentTeamMembers = planInfo?.usage?.teamMembers || 0;
  const isAtLimit = teamMemberLimit !== null && currentTeamMembers >= teamMemberLimit;
  const isNearLimit = teamMemberLimit !== null && currentTeamMembers >= teamMemberLimit * 0.8;

  const createTeamMutation = useMutation({
    mutationFn: (name: string) => teamApi.createTeam({ name }),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Bank created — let’s set it up.');
      setShowCreateModal(false);
      setTeamName('');
      // Open Institution Setup for the brand-new bank.
      if (response?.data?.id) setSetupTeamId(response.data.id);
    },
    onError: () => {
      toast.error('Failed to create. Please try again.');
    },
  });

  const handleSetupClose = () => {
    setSetupTeamId(null);
    // Refresh cards so the new bank's regulator/setup status shows immediately.
    queryClient.invalidateQueries({ queryKey: ['teams'] });
  };


  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Banks &amp; Credit Unions</h1>
          <p className="mt-2 text-gray-600">Manage the banks and credit unions you have access to. Click one to view members, invitations, and roles.</p>
        </div>
        <div className="flex items-center gap-3">
          {teams.length > 1 && (
            <Link
              to="/organization"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <BuildingOfficeIcon className="h-5 w-5 mr-2 text-gray-500" />
              Organization Overview
            </Link>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Bank or Credit Union
          </button>
        </div>
      </div>

      {/* Team Member Limit Warning */}
      {teamMemberLimit !== null && (
        <div className={`mb-6 rounded-lg p-4 ${
          isAtLimit
            ? 'bg-red-50 border border-red-200'
            : isNearLimit
            ? 'bg-yellow-50 border border-yellow-200'
            : 'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-start">
            {(isAtLimit || isNearLimit) && (
              <ExclamationTriangleIcon className={`h-5 w-5 mr-3 mt-0.5 ${
                isAtLimit ? 'text-red-600' : 'text-yellow-600'
              }`} />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                isAtLimit ? 'text-red-800' : isNearLimit ? 'text-yellow-800' : 'text-blue-800'
              }`}>
                Team Member Usage: {currentTeamMembers} / {teamMemberLimit}
              </p>
              {isAtLimit && (
                <p className="text-sm text-red-700 mt-1">
                  You've reached your team member limit.
                  <a href="/contact" className="font-medium underline ml-1">Contact us to add more members</a>
                </p>
              )}
              {isNearLimit && !isAtLimit && (
                <p className="text-sm text-yellow-700 mt-1">
                  You're approaching your team member limit.
                  <a href="/contact" className="font-medium underline ml-1">Contact us to upgrade</a>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {teamsLoading && (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Loading teams...</p>
        </div>
      )}

      {/* Error state */}
      {teamsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">Failed to load teams</p>
          <p className="text-red-600 text-sm mt-1">
            {teamsError instanceof Error ? teamsError.message : "Please try again later"}
          </p>
        </div>
      )}

      {/* Empty state */}
      {!teamsLoading && !teamsError && teams.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">You don't have any teams yet.</p>
          <p className="text-gray-500 text-sm mt-1">Create your first team to collaborate with others.</p>
        </div>
      )}

      {/* Teams grid */}
      {!teamsLoading && !teamsError && teams.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {teams.map((team: any) => (
            <Link
              key={team.id}
              to={`/teams/${team.id}`}
              className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:border-primary-500 border-2 border-transparent transition-all cursor-pointer block group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">{team.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{team.members.length} members</p>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {team.primaryRegulator && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                    {REGULATOR_LABEL[team.primaryRegulator] || team.primaryRegulator}
                  </span>
                )}
                {team.institutionSetupCompleted ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                    Setup complete
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
                    <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                    Setup needed
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-center text-sm text-gray-600">
                <span>{team._count?.uploads || 0} uploads</span>
                <span className="mx-2">•</span>
                <span>{team._count?.reports || 0} reports</span>
              </div>
              <div className="mt-3 text-xs text-primary-600 font-medium">
                Click to manage →
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Create team modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Bank or Credit Union</h2>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Bank or credit union name"
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => createTeamMutation.mutate(teamName)}
                disabled={!teamName}
                className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                Create
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Institution Setup for a newly-created bank */}
      {setupTeamId && (
        <InstitutionSetupWizard
          isOpen={!!setupTeamId}
          teamId={setupTeamId}
          onClose={handleSetupClose}
        />
      )}

    </div>
  );
}
